// NOTE: This page is maintained for legacy routes only.
// Primary tour routing now happens via ViewAll.jsx with inline tour confirmation modal.
// The tour sequence ordering logic has been integrated into ViewAll.jsx.

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import LayoutHeader from '../../layout/LayoutHeader';
import { getDataAPI, postDataAPI } from '../../utils/fetchData';

const TourRouter = () => {
  const navigate = useNavigate();
  const [shows, setShows] = useState([]);
  const [selectedShows, setSelectedShows] = useState([]);
  const [tourName, setTourName] = useState('');
  const [homeCity, setHomeCity] = useState('');
  const [bufferHours, setBufferHours] = useState(6);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [tourPlan, setTourPlan] = useState(null);

  useEffect(() => {
    loadShows();
  }, []);

  const loadShows = async () => {
    try {
      setLoading(true);
      const token = JSON.parse(localStorage.getItem('dizeeToken'));
      const response = await getDataAPI('user/tm/shows', token);
      
      if (response.data.status === 200 && response.data.data) {
        const allShows = Array.isArray(response.data.data) ? response.data.data : [response.data.data];
        const upcomingShows = allShows.filter(
          show => !['completed', 'cancelled'].includes(show.status)
        ).sort((a, b) => new Date(a.showDate) - new Date(b.showDate));
        setShows(upcomingShows);
      }
    } catch (error) {
      console.error('Error loading shows:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleShowToggle = (showId) => {
    setSelectedShows(prev => {
      if (prev.includes(showId)) {
        return prev.filter(id => id !== showId);
      } else {
        return [...prev, showId];
      }
    });
  };

  const handleReorder = (showId, direction) => {
    const currentIndex = selectedShows.indexOf(showId);
    if (
      (direction === 'up' && currentIndex === 0) ||
      (direction === 'down' && currentIndex === selectedShows.length - 1)
    ) {
      return;
    }

    const newOrder = [...selectedShows];
    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    [newOrder[currentIndex], newOrder[newIndex]] = [newOrder[newIndex], newOrder[currentIndex]];
    setSelectedShows(newOrder);
  };

  const generateTourPlan = async () => {
    if (selectedShows.length === 0) {
      alert('Please select at least one show for your tour.');
      return;
    }

    if (!homeCity.trim()) {
      alert('Please enter your home city for departure/return flights.');
      return;
    }

    setGenerating(true);
    try {
      const token = JSON.parse(localStorage.getItem('dizeeToken'));
      const response = await postDataAPI('user/tm/tour-router/generate', {
        showIds: selectedShows,
        tourName: tourName.trim() || undefined, // Optional, backend will generate default if not provided
        homeCity: homeCity.trim(),
        bufferHours: bufferHours
      }, token);

      if (response.data.status === 200) {
        console.log('✅ Tour plan response:', response.data);
        const { tourPlan, tourPlanId } = response.data.data;
        console.log('✅ Tour plan ID:', tourPlanId);
        console.log('✅ Tour plan data:', tourPlan);
        
        setTourPlan(tourPlan);
        
        if (!tourPlanId) {
          alert('Tour plan was generated but no ID was returned. Please check backend logs.');
          return;
        }
        
        // Navigate to tour overview page
        alert('Tour plan generated successfully! Redirecting to overview...');
        setTimeout(() => {
          console.log('🔄 Navigating to:', `/tour-manager/tour-overview/${tourPlanId}`);
          navigate(`/tour-manager/tour-overview/${tourPlanId}`);
        }, 1500);
      } else {
        throw new Error(response.data.message || 'Failed to generate tour plan');
      }
    } catch (error) {
      console.error('Error generating tour plan:', error);
      alert(error.message || 'Failed to generate tour plan. Please try again.');
    } finally {
      setGenerating(false);
    }
  };

  const executeAllSearches = async () => {
    if (!tourPlan) return;

    setGenerating(true);
    try {
      const token = JSON.parse(localStorage.getItem('dizeeToken'));
      
      // Execute all flight searches
      for (const flight of tourPlan.flights) {
        await postDataAPI('user/tm/flights/search', {
          show_id: flight.destinationShowId || null,
          origin: flight.from,
          destination: flight.to,
          departure_date: flight.departureDate,
          cabin_class: 'economy',
          passengers: 1
        }, token);
      }

      // Execute all hotel searches
      for (const hotel of tourPlan.hotels) {
        await postDataAPI('user/tm/hotels/search', {
          location: hotel.city,
          check_in_date: hotel.checkIn,
          check_out_date: hotel.checkOut,
          guests: 1,
          rooms: 1
        }, token);
      }

      alert(`Searches complete! Searched ${tourPlan.flights.length} flights and ${tourPlan.hotels.length} hotels.`);
    } catch (error) {
      console.error('Error executing searches:', error);
      alert('Some searches failed. Please try again.');
    } finally {
      setGenerating(false);
    }
  };

  const selectedShowsData = selectedShows
    .map(id => shows.find(s => s._id === id))
    .filter(Boolean);

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    });
  };

  const formatDateTime = (date) => {
    return new Date(date).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <LayoutHeader>
        <div className="flex justify-center w-full">
          <div className="w-[390px] h-[80vh] flex flex-col items-center justify-center">
            <p className="text-white text-[12px]">Loading shows...</p>
          </div>
        </div>
      </LayoutHeader>
    );
  }

  return (
    <LayoutHeader>
      <div className="flex justify-center w-full">
        <div className="w-[390px] h-[80vh] flex flex-col items-center relative z-[0]">

          {/* Header */}
          <div className="px-4 my-[50px] flex w-full justify-between items-center">
            <span className="text-white text-[14px]" style={{ fontWeight: 300 }}>Smart Tour Router</span>
            <div className="flex items-center gap-4">
              {tourPlan && (
                <button 
                  onClick={executeAllSearches}
                  disabled={generating}
                  className="text-[#FF9500] text-[12px] font-normal cursor-pointer hover:opacity-70"
                >
                  {generating ? 'Searching...' : 'Search All'}
                </button>
              )}
              
              <span 
                className="text-white text-[12px] font-normal cursor-pointer hover:opacity-70" 
                onClick={() => navigate(-1)}
              >
                Back
              </span>
            </div>
          </div>

          {/* Settings Panel - Always visible when no tour plan */}
          {!tourPlan && (
            <div className="w-full px-4 mb-6">
              <div className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4 space-y-4">
                <div className="flex items-center gap-2 mb-3">
                  <svg className="w-4 h-4 text-[#FF9500]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
                  </svg>
                  <span className="text-white text-[12px] font-normal">Tour Settings</span>
                </div>
                
                <div className="mb-4">
                  <label className="block text-[rgba(255,255,255,0.7)] text-[10px] mb-2">
                    Tour Name (Optional)
                  </label>
                  <input
                    type="text"
                    value={tourName}
                    onChange={(e) => setTourName(e.target.value)}
                    placeholder="e.g., Summer 2025 Tour (auto-generated if empty)"
                    className="w-full bg-transparent border border-[rgba(255,255,255,0.2)] rounded-lg px-3 py-2 text-white text-[12px] placeholder-[rgba(255,255,255,0.5)] focus:outline-none focus:border-[#FF9500]"
                  />
                </div>
                
                <div>
                  <label className="block text-[rgba(255,255,255,0.7)] text-[10px] mb-2">
                    Home City (Start/End Point) *
                  </label>
                  <input
                    type="text"
                    value={homeCity}
                    onChange={(e) => setHomeCity(e.target.value)}
                    placeholder="e.g., Los Angeles, CA"
                    className="w-full bg-transparent border border-[rgba(255,255,255,0.2)] rounded-lg px-3 py-2 text-white text-[12px] placeholder-[rgba(255,255,255,0.5)] focus:outline-none focus:border-[#FF9500]"
                  />
                </div>

                <div>
                  <label className="block text-[rgba(255,255,255,0.7)] text-[10px] mb-2">
                    Arrival Buffer: {bufferHours}h before show
                  </label>
                  <input
                    type="range"
                    min="2"
                    max="48"
                    value={bufferHours}
                    onChange={(e) => setBufferHours(parseInt(e.target.value))}
                    className="w-full h-1 bg-[rgba(255,255,255,0.2)] rounded-lg appearance-none cursor-pointer slider-thumb"
                    style={{
                      accentColor: '#FF9500'
                    }}
                  />
                  <p className="text-[rgba(255,255,255,0.5)] text-[10px] mt-1">
                    Time for soundcheck, setup, rest, etc.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Content */}
          <div className="w-full px-4 flex-1 overflow-y-auto pb-6">
            {tourPlan ? (
              /* Tour Plan Results */
              <div className="space-y-4">
                {/* Summary */}
                <div className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4">
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-white text-[12px] font-semibold">Tour Plan Generated</span>
                    <button
                      onClick={() => setTourPlan(null)}
                      className="text-[rgba(255,255,255,0.5)] hover:text-white text-[10px]"
                    >
                      Clear
                    </button>
                  </div>
                  
                  <div className="grid grid-cols-3 gap-2 mb-4">
                    <div className="bg-[rgba(255,149,0,0.1)] border border-[rgba(255,149,0,0.3)] rounded p-2 text-center">
                      <p className="text-[#FF9500] text-[16px] font-semibold">{tourPlan.flights?.length || 0}</p>
                      <p className="text-[rgba(255,255,255,0.5)] text-[10px]">Flights</p>
                    </div>
                    <div className="bg-[rgba(255,149,0,0.1)] border border-[rgba(255,149,0,0.3)] rounded p-2 text-center">
                      <p className="text-[#FF9500] text-[16px] font-semibold">{tourPlan.hotels?.length || 0}</p>
                      <p className="text-[rgba(255,255,255,0.5)] text-[10px]">Hotels</p>
                    </div>
                    <div className="bg-[rgba(255,149,0,0.1)] border border-[rgba(255,149,0,0.3)] rounded p-2 text-center">
                      <p className="text-[#FF9500] text-[16px] font-semibold">{tourPlan.totalDays || 0}</p>
                      <p className="text-[rgba(255,255,255,0.5)] text-[10px]">Days</p>
                    </div>
                  </div>
                </div>

                {/* Timeline */}
                <div className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4">
                  <span className="text-white text-[12px] font-semibold mb-3 block">Timeline</span>
                  
                  <div className="space-y-3">
                    {tourPlan.timeline?.map((item, index) => (
                      <div key={index} className="relative pl-6">
                        {/* Timeline line */}
                        {index < tourPlan.timeline.length - 1 && (
                          <div className="absolute left-2 top-5 w-px h-full bg-[rgba(255,255,255,0.1)]"></div>
                        )}

                        {/* Icon */}
                        <div className={`absolute left-0 top-0 w-4 h-4 rounded-full flex items-center justify-center ${
                          item.type === 'flight' ? 'bg-[#FF9500]' :
                          item.type === 'hotel' ? 'bg-[rgba(147,51,234,0.8)]' :
                          'bg-[rgba(34,197,94,0.8)]'
                        }`}>
                          <span className="text-[8px]">
                            {item.type === 'flight' ? '✈' : item.type === 'hotel' ? '🏨' : '🎸'}
                          </span>
                        </div>

                        {/* Content */}
                        <div>
                          <p className="text-[rgba(255,255,255,0.5)] text-[10px] mb-0.5">
                            {formatDateTime(item.dateTime)}
                          </p>
                          <p className="text-white text-[11px] font-medium">{item.title}</p>
                          {item.description && (
                            <p className="text-[rgba(255,255,255,0.5)] text-[10px] mt-0.5">{item.description}</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Warnings */}
                {tourPlan.warnings && tourPlan.warnings.length > 0 && (
                  <div className="bg-[rgba(255,149,0,0.1)] border border-[rgba(255,149,0,0.3)] rounded-lg p-3">
                    <div className="flex items-start gap-2">
                      <svg className="w-4 h-4 text-[#FF9500] flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                      </svg>
                      <div className="flex-1">
                        <p className="text-[#FF9500] text-[11px] font-semibold mb-1">Alerts</p>
                        <ul className="text-[rgba(255,255,255,0.7)] text-[10px] space-y-1">
                          {tourPlan.warnings.map((warning, index) => (
                            <li key={index}>• {warning}</li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              /* Show Selection */
              <div className="space-y-4">
                {/* Selected Shows Order */}
                {selectedShowsData.length > 0 && (
                  <div className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4">
                    <span className="text-white text-[12px] font-semibold mb-3 block">Tour Sequence ({selectedShowsData.length})</span>
                    
                    <div className="space-y-2 mb-4">
                      {selectedShowsData.map((show, index) => (
                        <div key={show._id} className="flex items-center gap-2 bg-[rgba(255,149,0,0.1)] border border-[rgba(255,149,0,0.3)] rounded p-2">
                          <div className="flex flex-col gap-0.5">
                            <button
                              onClick={() => handleReorder(show._id, 'up')}
                              disabled={index === 0}
                              className="text-[10px] text-[rgba(255,255,255,0.5)] hover:text-white disabled:opacity-30"
                            >
                              ▲
                            </button>
                            <button
                              onClick={() => handleReorder(show._id, 'down')}
                              disabled={index === selectedShowsData.length - 1}
                              className="text-[10px] text-[rgba(255,255,255,0.5)] hover:text-white disabled:opacity-30"
                            >
                              ▼
                            </button>
                          </div>

                          <div className="w-5 h-5 rounded-full bg-[#FF9500] flex items-center justify-center text-white text-[10px] font-bold">
                            {index + 1}
                          </div>

                          <div className="flex-1 min-w-0">
                            <p className="text-white text-[11px] font-medium truncate">
                              {show.venue?.city}, {show.venue?.state}
                            </p>
                            <p className="text-[rgba(255,255,255,0.5)] text-[10px]">
                              {formatDate(show.showDate)}
                            </p>
                          </div>

                          <button
                            onClick={() => handleShowToggle(show._id)}
                            className="text-[rgba(255,255,255,0.5)] hover:text-white text-[12px]"
                          >
                            ✕
                          </button>
                        </div>
                      ))}
                    </div>

                    <button
                      onClick={generateTourPlan}
                      disabled={generating || !homeCity.trim()}
                      className="w-full bg-[#FF9500] text-white px-4 py-2 rounded-lg text-[12px] hover:bg-[#FF8800] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      {generating ? 'Generating...' : 'Generate Tour Plan'}
                    </button>
                  </div>
                )}

                {/* Show Selection List */}
                <div className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4">
                  <span className="text-white text-[12px] font-semibold mb-3 block">Select Shows</span>
                  
                  {shows.length === 0 ? (
                    <p className="text-center text-[rgba(255,255,255,0.5)] text-[11px] py-8">
                      No upcoming shows found
                    </p>
                  ) : (
                    <div className="space-y-2">
                      {shows.map((show) => {
                        const isSelected = selectedShows.includes(show._id);
                        const selectionIndex = selectedShows.indexOf(show._id);

                        return (
                          <div
                            key={show._id}
                            onClick={() => handleShowToggle(show._id)}
                            className={`p-3 border rounded-lg cursor-pointer transition-all ${
                              isSelected
                                ? 'border-[#FF9500] bg-[rgba(255,149,0,0.1)]'
                                : 'border-[rgba(255,255,255,0.1)] hover:border-[rgba(255,255,255,0.2)]'
                            }`}
                          >
                            <div className="flex items-start gap-3">
                              <div className={`flex-shrink-0 w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                                isSelected
                                  ? 'border-[#FF9500] bg-[#FF9500] text-white'
                                  : 'border-[rgba(255,255,255,0.3)]'
                              }`}>
                                {isSelected && (
                                  <span className="text-[10px] font-bold">{selectionIndex + 1}</span>
                                )}
                              </div>
                              
                              <div className="flex-1 min-w-0">
                                <p className="text-[rgba(255,255,255,0.7)] text-[10px] mb-1">
                                  {formatDate(show.showDate)}
                                </p>
                                <p className="text-white text-[11px] font-medium">
                                  {show.venue?.name || 'Unknown Venue'}
                                </p>
                                <p className="text-[rgba(255,255,255,0.5)] text-[10px]">
                                  {show.venue?.city}, {show.venue?.state || show.venue?.country}
                                </p>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </LayoutHeader>
  );
};

export default TourRouter;
